import React, { useState } from "react";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import { toast } from "sonner";
import { STATUS_COLORS, STATUS_LABELS, trackOrder } from "../lib/api";

const FLOW = ["pending", "picked_up", "washing", "out_for_delivery", "delivered"];

function Timeline({ status }) {
  const idx = FLOW.indexOf(status);
  return (
    <div className="mt-8">
      <div className="flex items-center">
        {FLOW.map((s, i) => (
          <React.Fragment key={s}>
            <div
              className={`w-4 h-4 rounded-full flex-shrink-0 border ${
                i <= idx ? "border-rw-ink" : "border-rw-ink/20"
              }`}
              style={{ background: i <= idx ? STATUS_COLORS[s].bg : "transparent" }}
            />
            {i < FLOW.length - 1 && (
              <div className={`flex-1 h-px ${i < idx ? "bg-rw-ink" : "bg-rw-ink/15"}`} />
            )}
          </React.Fragment>
        ))}
      </div>
      <div className="grid grid-cols-5 mt-2 text-[10px] md:text-xs text-rw-ink2 gap-1">
        {FLOW.map((s, i) => (
          <div key={s} className={i <= idx ? "text-rw-ink font-semibold" : ""}>
            {STATUS_LABELS[s]}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Track() {
  const [q, setQ] = useState("");
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);

  const search = async (e) => {
    e?.preventDefault();
    if (!q.trim()) return;
    setLoading(true);
    setOrder(null);
    try {
      const res = await trackOrder(q.trim());
      setOrder(res.order);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Order not found. Check the code or phone number.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-rw-bg text-rw-ink" data-testid="track-page">
      <Seo
        title="Track Your Laundry Order"
        description="Track your RIZ N WASH laundry order in real time. Enter your order code or phone number to see pickup, wash, and delivery status for your Gurugram doorstep laundry."
        path="/track"
        noindex
      />
      <Nav />

      <div className="max-w-4xl mx-auto px-6 md:px-10 pt-16 md:pt-24">
        <div className="overline mb-3">Track order</div>
        <h1 className="font-display text-4xl md:text-6xl font-light tracking-tight leading-[1.05]">
          Where&apos;s my <em className="italic text-rw-blue not-italic">laundry</em>?
        </h1>

        <form onSubmit={search} className="mt-10 flex flex-col sm:flex-row gap-3" data-testid="track-form">
          <input
            className="flex-1 rounded-full border border-rw-ink/20 bg-white px-5 py-3.5 rw-focus"
            placeholder="Order code (RW…) or phone number"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="track-input"
          />
          <button
            type="submit"
            disabled={loading}
            className="btn-dark rounded-full px-6 py-3.5 text-sm font-semibold"
            data-testid="track-submit-btn"
          >
            {loading ? "Searching..." : "Track order"}
          </button>
        </form>

        {order && (
          <div className="mt-12 rounded-2xl border border-rw-ink/10 bg-rw-bg2 p-6 md:p-8" data-testid="track-result">
            <div className="flex flex-wrap items-baseline justify-between gap-4">
              <div>
                <div className="overline">Order</div>
                <div className="font-display text-3xl md:text-4xl mt-1" data-testid="track-order-code">
                  {order.order_code}
                </div>
              </div>
              <div className="text-right">
                <div className="overline">Status</div>
                <div
                  className="mt-1 inline-block px-3 py-1 rounded-full font-display text-sm"
                  style={{ background: STATUS_COLORS[order.status]?.bg || "#eee", color: "#1A1A24" }}
                  data-testid="track-status-badge"
                >
                  {STATUS_LABELS[order.status] || order.status}
                </div>
              </div>
            </div>

            {order.status !== "cancelled" && <Timeline status={order.status} />}

            <div className="grid md:grid-cols-3 gap-6 mt-10 text-sm">
              <div>
                <div className="overline">Customer</div>
                <div className="mt-1">{order.customer_name}</div>
                <div className="text-rw-ink2">{order.phone}</div>
              </div>
              <div>
                <div className="overline">Pickup</div>
                <div className="mt-1">{order.pickup_date}</div>
                <div className="text-rw-ink2">{order.pickup_slot}</div>
              </div>
              <div>
                <div className="overline">Address</div>
                <div className="mt-1">{order.address}</div>
                {order.sector && <div className="text-rw-ink2">{order.sector}</div>}
              </div>
            </div>

            <div className="border-t border-rw-ink/10 mt-8 pt-4">
              {order.items.map((it) => (
                <div key={it.service_id} className="flex justify-between text-sm py-1">
                  <span>{it.name} <span className="text-rw-ink3">× {it.qty}</span></span>
                  <span>₹{(it.qty * it.unit_price).toFixed(0)}</span>
                </div>
              ))}
              <div className="flex justify-between font-display text-xl md:text-2xl py-2 border-t border-rw-ink/10 mt-2">
                <span>Total</span>
                <span>₹{order.total}</span>
              </div>
              <div className="text-xs text-rw-ink3 mt-1">
                Payment: {order.payment_status === "paid" ? "Paid" : "Pay on delivery / UPI"}
              </div>
            </div>
          </div>
        )}

        {!order && (
          <p className="mt-6 text-sm text-rw-ink2">
            Tip: your order code was sent to you right after booking (starts with <span className="font-mono">RW</span>).
          </p>
        )}
      </div>

      {/* SEO content */}
      <section className="max-w-4xl mx-auto px-6 md:px-10 py-16 border-t border-rw-ink/10 mt-16">
        <div className="overline mb-3">Order tracking, explained</div>
        <h2 className="font-display text-3xl md:text-4xl font-light tracking-tight leading-[1.1]">
          Follow every wash, from pickup to delivery.
        </h2>
        <div className="mt-6 grid md:grid-cols-2 gap-8 text-sm text-rw-ink2 leading-relaxed">
          <div>
            <p>
              Every RIZ N WASH laundry order moves through five stages: <strong className="text-rw-ink">pending pickup</strong>, <strong className="text-rw-ink">picked up</strong>,
              <strong className="text-rw-ink"> in wash</strong>, <strong className="text-rw-ink">out for delivery</strong>, and finally <strong className="text-rw-ink">delivered</strong>.
              You will get a WhatsApp update at each step, and the same information is available on this page in real time.
            </p>
            <p className="mt-3">
              If your order is not moving as expected — for example, still marked as pending 15 minutes past your slot —
              give us a call or reply to the WhatsApp confirmation. Our operations team responds within a few minutes
              on weekdays and typically within 15 minutes on Sunday mornings, which is our peak pickup window.
            </p>
          </div>
          <div>
            <p>
              You can search by your <strong className="text-rw-ink">order code</strong> (something like <span className="font-mono">RWABC123</span>) or by your
              <strong className="text-rw-ink"> phone number</strong>. Searching by phone always returns your most recent order — perfect if you
              have misplaced the code.
            </p>
            <p className="mt-3">
              Once delivered, orders remain trackable for 90 days so you can reference them for a repeat booking. Your
              order also includes a personal referral code — share it with a neighbour and both of you earn credit that
              works on your next pickup or on any RIZ N WASH weekly subscription.
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
